import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { User, UserPayload } from '../types';
import queryKeys from './queryKeys';
import type { MutationConfig } from 'lib/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import { isAxiosError } from 'axios';

interface UpdateUserParams {
  id: number;
  data: UserPayload;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

const updateUser = async (params: UpdateUserParams): Promise<User> => {
  const res = await apiClient.put<User>(
    `${queryKeys.users}/${params.id}`,
    params.data,
  );
  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update user');
  }

  return res.data;
};

interface UseUpdateUserOptions {
  config?: MutationConfig<typeof updateUser>;
}

export const useUpdateUser = ({ config }: UseUpdateUserOptions) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.users],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.userDetails],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateUser,
    mutationKey: [queryKeys.users],
  });
};
